import { LoginSchema } from "@gosaas/schemas";
import type { ConstantsLoginStruct, ConstantsOAuthStruct } from "@gosaas/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { AuthOauthSection } from "@/components/auth/auth-oauth-section";
import { AuthPage } from "@/components/auth/auth-page";
import { useAuth } from "@/components/providers/auth";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import { PasswordInput } from "@/components/ui/input-password";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	isAuthOAuthGitHubEnabled,
	isAuthOAuthGoogleEnabled,
} from "@/lib/modules";
import { cn } from "@/lib/utils";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import {
	getLastSignInMethod,
	type LastSignInMethod,
	setAccessToken,
	setLastOauthProvider,
	setLastSignInMethod,
	setRefreshToken,
} from "@/utils/local-storage";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/auth/login")({
	component: Login,
	head: () =>
		seo({
			title: "Login",
			description: "Login to your account",
			keywords: "login, account, sign in, sign up",
		}),
});

export function showAnyAuthOAuthProvider(): boolean {
	return isAuthOAuthGoogleEnabled() || isAuthOAuthGitHubEnabled();
}

export function shouldEmphasizeEmailSignIn(
	lastSignInMethod: LastSignInMethod | null,
): boolean {
	return (
		lastSignInMethod === "email" ||
		(lastSignInMethod === "google" && !isAuthOAuthGoogleEnabled()) ||
		(lastSignInMethod === "github" && !isAuthOAuthGitHubEnabled())
	);
}

export function Login() {
	const router = useRouter();
	const { refresh } = useAuth();
	const [lastSignInMethod, setLastSignInMethodState] =
		useState<LastSignInMethod | null>(() => getLastSignInMethod());

	const showAnyOAuthProvider = showAnyAuthOAuthProvider();
	const emphasizeEmailSignIn = shouldEmphasizeEmailSignIn(lastSignInMethod);

	const form = useForm<z.infer<typeof LoginSchema>>({
		resolver: zodResolver(LoginSchema),
		defaultValues: {
			email: "",
			password: "",
			otp: "",
			recoveryCode: "",
		},
	});

	const fetchGithubOauth = async () => {
		if (!isAuthOAuthGitHubEnabled()) {
			toast.error("Couldn't sign in with GitHub", {
				description: "This sign-in method is not enabled.",
			});
			return;
		}
		try {
			const response = await requestWithHandling<ConstantsOAuthStruct>(() =>
				apiClient.api.oauthGithubList(),
			);
			if (response.data?.url) {
				setLastOauthProvider("github");
				setLastSignInMethodState("github");
				window.location.href = response.data.url;
			} else {
				toast.error("Couldn't sign in with GitHub", {
					description: "Received an invalid response from the server.",
				});
			}
		} catch (error) {
			toast.error("Couldn't sign in with GitHub", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const fetchGoogleOauth = async () => {
		if (!isAuthOAuthGoogleEnabled()) {
			toast.error("Couldn't sign in with Google", {
				description: "This sign-in method is not enabled.",
			});
			return;
		}
		try {
			const response = await requestWithHandling<ConstantsOAuthStruct>(() =>
				apiClient.api.oauthGoogleList(),
			);
			if (response.data?.url) {
				setLastOauthProvider("google");
				setLastSignInMethodState("google");
				window.location.href = response.data.url;
			} else {
				toast.error("Couldn't sign in with Google", {
					description: "Received an invalid response from the server.",
				});
			}
		} catch (error) {
			toast.error("Couldn't sign in with Google", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const onSubmit = async (data: z.infer<typeof LoginSchema>) => {
		try {
			const response = await requestWithHandling<ConstantsLoginStruct>(() =>
				apiClient.api.authLoginCreate({
					email: data.email,
					password: data.password,
					otp: data.otp,
					recovery_key: data.recoveryCode,
				}),
			);
			const responseData = response.data;
			if (responseData) {
				setLastSignInMethod("email");
				setLastSignInMethodState("email");
				setAccessToken(responseData.access_token);
				setRefreshToken(responseData.refresh_token);
				toast.success("Logged in successfully", {
					description: "Welcome back!",
				});
				refresh();
				router.navigate({
					to: "/dashboard",
				});
			} else {
				toast.error("Couldn't sign in", {
					description: "Received an unexpected response from the server.",
				});
			}
		} catch (error) {
			toast.error("Couldn't sign in", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<AuthPage
			title="Sign in"
			description={
				showAnyOAuthProvider
					? "Use your email or a connected account."
					: "Use your email and password."
			}
		>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-5"
					noValidate
				>
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem
								className={cn(
									emphasizeEmailSignIn &&
										"space-y-2 border-l-2 border-primary/40 py-0.5 pl-3.5 dark:border-primary/50",
								)}
							>
								<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
									<FormLabel className="text-foreground">Email</FormLabel>
									{emphasizeEmailSignIn ? (
										<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
											<span
												className="size-1.5 shrink-0 rounded-full bg-primary"
												aria-hidden
											/>
											<span>Last used</span>
										</div>
									) : null}
								</div>
								<FormControl>
									<Input
										{...field}
										type="email"
										autoComplete="email"
										required
										autoFocus={emphasizeEmailSignIn}
										className={cn(
											emphasizeEmailSignIn &&
												"border-primary/20 ring-1 ring-primary/10 focus-visible:border-primary/35 focus-visible:ring-primary/25",
										)}
									/>
								</FormControl>
								{emphasizeEmailSignIn ? (
									<p className="text-xs text-muted-foreground">
										Same sign-in method as last time — email and password.
									</p>
								) : null}
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<div className="flex items-baseline justify-between gap-2">
									<FormLabel>Password</FormLabel>
									<Link
										to="/auth/password-reset"
										className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
									>
										Forgot password?
									</Link>
								</div>
								<FormControl>
									<PasswordInput
										{...field}
										required
										autoComplete="current-password"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="space-y-3">
						<p className="text-sm font-medium text-foreground">
							Two-factor (optional)
						</p>
						<Tabs defaultValue="app" className="w-full">
							<TabsList className="grid h-9 w-full grid-cols-2">
								<TabsTrigger value="app" type="button">
									App code
								</TabsTrigger>
								<TabsTrigger value="recovery" type="button">
									Recovery
								</TabsTrigger>
							</TabsList>
							<TabsContent value="app" className="mt-4">
								<FormField
									control={form.control}
									name="otp"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<InputOTP
													maxLength={6}
													value={field.value}
													onChange={(value) => field.onChange(value)}
												>
													<InputOTPGroup>
														<InputOTPSlot index={0} />
														<InputOTPSlot index={1} />
														<InputOTPSlot index={2} />
													</InputOTPGroup>
													<InputOTPSeparator />
													<InputOTPGroup>
														<InputOTPSlot index={3} />
														<InputOTPSlot index={4} />
														<InputOTPSlot index={5} />
													</InputOTPGroup>
												</InputOTP>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</TabsContent>
							<TabsContent value="recovery" className="mt-4">
								<FormField
									control={form.control}
									name="recoveryCode"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input
													placeholder="Recovery code"
													className="font-mono"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</TabsContent>
						</Tabs>
					</div>

					<Button type="submit" className="h-11 w-full cursor-pointer">
						Sign in
					</Button>
				</form>
			</Form>

			<AuthOauthSection
				lastSignInMethod={lastSignInMethod}
				onGoogle={fetchGoogleOauth}
				onGithub={fetchGithubOauth}
			/>

			<p className="mt-8 text-center text-base text-muted-foreground">
				No account?{" "}
				<Link
					to="/auth/register"
					className="font-medium text-foreground underline-offset-4 hover:underline"
				>
					Create one
				</Link>
			</p>
		</AuthPage>
	);
}
